import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { getFighterDashboard, updateFighterProfile, trackFeatureUsage } from '../services/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  User, 
  ArrowLeft, 
  Save, 
  Instagram, 
  Twitter, 
  Youtube, 
  Facebook, 
  Globe,
  Camera,
  Loader2,
  Bell,
  BellOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';

// Push Notification Toggle Component
const PushNotificationToggle = () => {
  const { isSupported, isSubscribed, isLoading, permission, subscribe, unsubscribe } = usePushNotifications();
  
  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };
  
  if (!isSupported) {
    return null;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6"
    >
      <h2 className="font-heading text-xl text-white uppercase tracking-tight mb-4">
        Notifications
      </h2>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-zinc-500" />
          )}
          <div>
            <p className="text-white font-medium">Push Notifications</p>
            <p className="text-zinc-400 text-sm">
              {permission === 'denied' 
                ? 'Notifications blocked in browser settings'
                : isSubscribed 
                  ? 'Get notified when coaches leave notes'
                  : 'Enable to get instant alerts'
              }
            </p>
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading || permission === 'denied'}
          className="data-[state=checked]:bg-red-600"
        />
      </div>
    </motion.div>
  );
};

const FighterProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fighter, setFighter] = useState(null);
  
  const [formData, setFormData] = useState({
    bio: '',
    profile_image: '',
    social_links: {
      instagram: '',
      twitter: '',
      youtube: '',
      facebook: '',
      tiktok: '',
      website: ''
    }
  });

  useEffect(() => {
    if (user?.role !== 'fighter') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const res = await getFighterDashboard();
      if (res.data.linked && res.data.fighter) {
        const f = res.data.fighter;
        setFighter(f);
        setFormData({
          bio: f.bio || '',
          profile_image: f.profile_image || '',
          social_links: {
            instagram: f.social_links?.instagram || '',
            twitter: f.social_links?.twitter || '',
            youtube: f.social_links?.youtube || '',
            facebook: f.social_links?.facebook || '',
            tiktok: f.social_links?.tiktok || '',
            website: f.social_links?.website || ''
          }
        });
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Clean up empty social links
      const cleanedSocialLinks = {};
      Object.entries(formData.social_links).forEach(([key, value]) => {
        if (value.trim()) {
          cleanedSocialLinks[key] = value.trim();
        }
      });
      
      await updateFighterProfile({
        bio: formData.bio || null,
        profile_image: formData.profile_image || null,
        social_links: Object.keys(cleanedSocialLinks).length > 0 ? cleanedSocialLinks : null
      });
      
      toast.success('Profile updated!');
      trackFeatureUsage('fighter_profile', 'update').catch(() => {});
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setSaving(false);
    }
  };

  const updateSocialLink = (platform, value) => {
    setFormData({
      ...formData,
      social_links: {
        ...formData.social_links,
        [platform]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!fighter) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">No fighter profile linked</p>
          <Link to="/fighter/dashboard">
            <Button variant="outline" className="border-zinc-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/fighter/dashboard">
            <Button variant="ghost" className="text-zinc-400">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="font-heading text-lg text-white uppercase">Edit Profile</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Info */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6"
          >
            <h2 className="font-heading text-xl text-white uppercase tracking-tight mb-6">
              Profile Information
            </h2>
            
            <div className="flex items-start gap-6 mb-6">
              <div className="w-24 h-24 bg-zinc-800 rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                {formData.profile_image ? (
                  <img 
                    src={formData.profile_image} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-zinc-600" />
                )}
              </div>
              <div className="flex-1">
                <Label className="text-zinc-300">Profile Image URL</Label>
                <Input
                  value={formData.profile_image}
                  onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                  placeholder="https://example.com/your-photo.jpg"
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Enter a URL to your profile photo
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Bio</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about yourself, your fighting style, achievements..."
                className="bg-zinc-950 border-zinc-800 focus:border-red-600 min-h-[120px]"
              />
            </div>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6"
          >
            <h2 className="font-heading text-xl text-white uppercase tracking-tight mb-6">
              Social Media Links
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  Instagram
                </Label>
                <Input
                  value={formData.social_links.instagram}
                  onChange={(e) => updateSocialLink('instagram', e.target.value)}
                  placeholder="https://instagram.com/username"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-blue-400" />
                  Twitter / X
                </Label>
                <Input
                  value={formData.social_links.twitter}
                  onChange={(e) => updateSocialLink('twitter', e.target.value)}
                  placeholder="https://twitter.com/username"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-red-500" />
                  YouTube
                </Label>
                <Input
                  value={formData.social_links.youtube}
                  onChange={(e) => updateSocialLink('youtube', e.target.value)}
                  placeholder="https://youtube.com/@channel"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook
                </Label>
                <Input
                  value={formData.social_links.facebook}
                  onChange={(e) => updateSocialLink('facebook', e.target.value)}
                  placeholder="https://facebook.com/username"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  TikTok
                </Label>
                <Input
                  value={formData.social_links.tiktok}
                  onChange={(e) => updateSocialLink('tiktok', e.target.value)}
                  placeholder="https://tiktok.com/@username"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-400" />
                  Website
                </Label>
                <Input
                  value={formData.social_links.website}
                  onChange={(e) => updateSocialLink('website', e.target.value)}
                  placeholder="https://yourwebsite.com"
                  className="bg-zinc-950 border-zinc-800 focus:border-red-600"
                />
              </div>
            </div>
          </motion.div>

          {/* Push Notifications */}
          <PushNotificationToggle />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 uppercase font-heading tracking-wider px-8"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default FighterProfilePage;
